import React, { useState } from 'react';

import { Button, Typography } from '@material-ui/core';

import { ProductOverride } from '../../screens/ProducePriceRules/ProducePriceRules.web';

export type ProductKind = ProductOverride['kind'];
export type ProductNumberField = 'windowDays' | 'caseAddOn' | 'fixedPrice' | 'floor' | 'ceiling';

const PRODUCT_KINDS: Array<{ value: ProductKind; label: string }> = [
  { value: 'loose', label: 'Loose item' },
  { value: 'case', label: 'Case SKU' },
];

const PRODUCT_FIELDS: Array<{ key: ProductNumberField; label: string; caseOnly?: boolean }> = [
  { key: 'windowDays', label: 'window days' },
  { key: 'caseAddOn', label: 'case add-on', caseOnly: true },
  { key: 'fixedPrice', label: 'fixed price' },
  { key: 'floor', label: 'floor' },
  { key: 'ceiling', label: 'ceiling' },
];

const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 };

const toNullableNumber = (value: string): number | null => (value.trim() === '' ? null : Number(value));

export const newProductOverride = (productNumber: string, kind: ProductKind): ProductOverride => {
  const common = {
    productNumber,
    windowDays: null,
    fixedPrice: null,
    floor: null,
    ceiling: null,
    transportation: null,
    waste: null,
    other: null,
  };
  return kind === 'case' ? { ...common, kind: 'case', caseAddOn: null } : { ...common, kind: 'loose' };
};

export const ProduceProductRules = (props: {
  products: ProductOverride[];
  canSetFixedPrice: boolean;
  onChangeProduct: (productNumber: string, field: ProductNumberField, value: number | null) => void;
  onAddProduct: (productNumber: string, kind: ProductKind) => void;
}) => {
  const { products, canSetFixedPrice, onChangeProduct, onAddProduct } = props;
  const [newProductNumber, setNewProductNumber] = useState('');
  const [newProductKind, setNewProductKind] = useState<ProductKind>('loose');

  const addProduct = () => {
    const productNumber = newProductNumber.trim();
    if (!productNumber) {
      return;
    }
    onAddProduct(productNumber, newProductKind);
    setNewProductNumber('');
  };

  return (
    <div style={{ marginTop: 12 }}>
      <Typography variant="subtitle2">Product rules</Typography>
      {products.map((product) => (
        <div key={product.productNumber} style={rowStyle}>
          <span style={{ minWidth: 200 }}>
            {product.productNumber} ({product.kind === 'case' ? 'Case SKU' : 'Loose item'})
          </span>
          {PRODUCT_FIELDS.filter((field) => !field.caseOnly || product.kind === 'case').map((field) => {
            const value = (product as Record<string, unknown>)[field.key] as number | null | undefined;
            return (
              <input
                key={field.key}
                type="number"
                aria-label={`${product.productNumber} ${field.label}`}
                placeholder={field.label}
                value={value === null || value === undefined ? '' : value}
                disabled={field.key === 'fixedPrice' && !canSetFixedPrice}
                onChange={(event) => onChangeProduct(product.productNumber, field.key, toNullableNumber(event.target.value))}
                style={{ width: 88 }}
              />
            );
          })}
        </div>
      ))}
      <div style={rowStyle}>
        <input
          type="text"
          aria-label="New product number"
          placeholder="Product number"
          value={newProductNumber}
          onChange={(event) => setNewProductNumber(event.target.value)}
          style={{ width: 120 }}
        />
        <select
          aria-label="New product kind"
          value={newProductKind}
          onChange={(event) => setNewProductKind(event.target.value as ProductKind)}
        >
          {PRODUCT_KINDS.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
        </select>
        <Button variant="outlined" aria-label="Add product" onClick={addProduct}>Add product</Button>
      </div>
    </div>
  );
};
